import { useEffect, useRef } from 'react';
import { base44 } from '@/api/base44Client';

// A suspended iPad drops its realtime socket while the screen is asleep or the
// installed app sits in the background — and a socket that is gone often still
// reports itself as open. A plain subscribe() call therefore freezes the venue
// screens until somebody closes and reopens the app, which is exactly what must
// never be needed. This hook adds the three things that were missing: a fresh
// snapshot and a rebuilt connection the moment the screen comes back, and a
// background sweep that notices a silently dead socket and re-reads rather than
// leaving stale values on screen.
const DEFAULT_POLL_MS = 10000;
// Waking and focusing can fire together; one re-read covers both.
const RESUME_THROTTLE_MS = 1500;

export default function useResilientSubscription({
  entities = [],
  onEvent,
  onResync,
  enabled = true,
  pollMs = DEFAULT_POLL_MS,
  // Screens carrying state that changes on other devices (a bay's session, a
  // booking) re-read on every sweep. Boards fed by a stream that already pushes
  // constantly only poll once that stream has actually gone quiet.
  pollWhileActive = false,
}) {
  const onEventRef = useRef(onEvent);
  const onResyncRef = useRef(onResync);
  useEffect(() => { onEventRef.current = onEvent; }, [onEvent]);
  useEffect(() => { onResyncRef.current = onResync; }, [onResync]);

  const entityKey = entities.join(',');

  useEffect(() => {
    if (!enabled || !entityKey) return undefined;
    const names = entityKey.split(',');
    const silentReconnectMs = Math.max(30000, pollMs * 4);
    let unsubscribes = [];
    // Traffic only counts when a real event arrives — a re-read we triggered
    // ourselves must never look like proof that the socket is still alive.
    let lastTrafficAt = Date.now();
    let lastResyncAt = 0;

    const connect = () => {
      unsubscribes.forEach((off) => { try { off(); } catch { /* already gone */ } });
      unsubscribes = names.map((name) =>
        base44.entities[name].subscribe((event) => {
          lastTrafficAt = Date.now();
          onEventRef.current?.(event, name);
        })
      );
    };

    const resync = (force = false) => {
      const now = Date.now();
      if (!force && now - lastResyncAt < RESUME_THROTTLE_MS) return;
      lastResyncAt = now;
      onResyncRef.current?.();
    };

    connect();

    // Coming back from sleep is the moment the connection is least trustworthy,
    // so the socket is rebuilt rather than reused, and a snapshot is taken.
    const onWake = () => {
      if (document.visibilityState !== 'visible') return;
      connect();
      resync(true);
    };
    const onOnline = () => { connect(); resync(true); };
    const onFocus = () => resync();

    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('pageshow', onWake);
    window.addEventListener('online', onOnline);
    window.addEventListener('focus', onFocus);

    const sweep = setInterval(() => {
      if (document.hidden) return;
      const idle = Date.now() - lastTrafficAt;
      if (!pollWhileActive && idle < pollMs) return;
      resync(true);
      // Quiet for several sweeps running means the socket is dead even though it
      // reports as open — rebuild it instead of polling forever.
      if (idle >= silentReconnectMs) {
        connect();
        lastTrafficAt = Date.now();
      }
    }, pollMs);

    return () => {
      clearInterval(sweep);
      document.removeEventListener('visibilitychange', onWake);
      window.removeEventListener('pageshow', onWake);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('focus', onFocus);
      unsubscribes.forEach((off) => { try { off(); } catch { /* already gone */ } });
    };
  }, [enabled, entityKey, pollMs, pollWhileActive]);
}